import React, { ChangeEvent, FunctionComponent, useCallback, useState } from "react";

import type { InvalidQuestionWarning } from ".";
import { AnswerTemplate } from "../../db/models/Answer";
import Question, { Option } from "../../db/models/Question";
import { useTranslate, useLanguage } from "../translation/frontend";
import ErrorMessage from "./ErrorMessage";
import QuestionOptions from "./QuestionOptions";
import TextInput from "./TextInput";
import { Translated, translateDbString } from "./Translated";
import { onlyIfMatches } from "./page-logic";

interface QuestionProps {
  question: Question;
  answers: Record<string, AnswerTemplate[]>;
  onInvalid: (warning: InvalidQuestionWarning) => void;
  onValid: (warning: InvalidQuestionWarning) => void;
}

function QuestionInput({
  question,
  answers,
  onInvalid,
  onValid,
}: {
  question: Question;
  answers: AnswerTemplate[];
  onInvalid: (warning: InvalidQuestionWarning) => void;
  onValid: (warning: InvalidQuestionWarning) => void;
}) {
  const translate = useTranslate();
  const [isInvalid, setIsInvalid] = useState(false);

  const handleOnInvalid = useCallback(
    (option?: Option) => {
      onInvalid({ question, option });
      setIsInvalid(true);
    },
    [onInvalid, question],
  );

  const handleOnValid = useCallback(
    (option?: Option) => {
      onValid({ question, option });
      setIsInvalid(false);
    },
    [onValid, question],
  );

  const handleOnInvalidFile = useCallback(() => handleOnInvalid, [handleOnInvalid]);

  switch (question.questionType) {
    case "TEXT":
    case "EMAIL":
    case "PHONE":
    case "DATE":
    case "DATETIME":
    case "TIME":
    case "TEXTAREA":
      return (
        <TextInput
          type={question.questionType}
          id={question.id}
          name={question.id}
          defaultValue={answers[0]?.freeText}
          required={question.required}
          onInvalid={handleOnInvalid}
          onValid={handleOnValid}
          characterLimit={question.characterLimit}
        />
      );
    case "RADIO":
      return (
        <QuestionOptions
          questionId={question.id}
          answers={answers}
          optionGroups={question.optionGroups!}
          inputType="radio"
          required={question.required}
          onInvalid={handleOnInvalid}
          onValid={handleOnValid}
        />
      );
    case "CHECKLIST":
      return (
        <QuestionOptions
          questionId={question.id}
          answers={answers}
          optionGroups={question.optionGroups!}
          inputType="checkbox"
          required={question.required}
          onInvalid={handleOnInvalid}
          onValid={handleOnValid}
        />
      );
    case "IMAGE": {
      const uploadedFiles = answers.filter((answer) => answer !== "");
      return (
        <>
          {isInvalid ? <ErrorMessage type={question.questionType} id={question.id} /> : null}
          <input
            className="form-control"
            id={question.id}
            name={question.id}
            required={question.required && uploadedFiles.length === 0}
            type="file"
            accept="image/*"
            multiple
            onChange={(ev: ChangeEvent<HTMLInputElement>) => {
              if (
                ev.currentTarget.files &&
                Array.from(ev.currentTarget.files).reduce((a, file) => a + file.size, 0) > 25_000_000
              ) {
                alert(translate("filesTooLargeError"));
                ev.currentTarget.value = "";
              }
            }}
            onInvalid={handleOnInvalidFile}
          />
          {uploadedFiles.length > 0 ? <p>{translate("selectedFileCount", { count: uploadedFiles.length })}</p> : null}
        </>
      );
    }
    default:
      throw new Error("unknown question type");
  }
}

export function QuestionTitle({
  question,
  answers,
}: {
  question: Question;
  answers: Record<string, AnswerTemplate[]>;
}) {
  for (const contextAwareTitle of question.contextAwareTitles) {
    if (onlyIfMatches(contextAwareTitle.when, answers)) {
      return <Translated translations={contextAwareTitle.titleTranslations}>{contextAwareTitle.title}</Translated>;
    }
  }

  return <Translated translations={question.titleTranslations}>{question.title}</Translated>;
}

function QuestionRequiredPrompt({ question }: { question: Question }) {
  return question.requiredPrompt ? (
    <span className="subtext">
      <Translated translations={question.requiredPromptTranslations}>{question.requiredPrompt}</Translated>
    </span>
  ) : null;
}

function QuestionAdditionalInfo({ question }: { question: Question }) {
  const language = useLanguage();

  return question.additionalInfo ? (
    <div
      className="question-text"
      dangerouslySetInnerHTML={{
        __html: translateDbString(question.additionalInfoTranslations, language) ?? question.additionalInfo,
      }}
    />
  ) : null;
}

const QuestionComponent: FunctionComponent<QuestionProps> = ({ question, answers, onInvalid, onValid }) => (
  <div className="field" id={`${question.id}-container`}>
    <label className="question" htmlFor={question.id}>
      <QuestionTitle question={question} answers={answers} />
      <QuestionRequiredPrompt question={question} />
    </label>
    <QuestionAdditionalInfo question={question} />
    <QuestionInput question={question} answers={answers[question.id] || []} onInvalid={onInvalid} onValid={onValid} />
  </div>
);

export default QuestionComponent;
